/**
 * Classification for Cal.com failures that are worth retrying.
 *
 * Cal fronts `api.cal.com` with Cloudflare, so an overloaded origin answers with a 5xx HTML
 * interstitial — 524 "A timeout occurred" in practice — instead of a Cal error payload. Those are
 * transient by definition, but a retry is only safe when replaying the request is safe, which is
 * why the method is part of the decision rather than the status alone.
 */

import { CalApiError, CalApiRateLimitError } from "./types";

/**
 * Only methods that are safe to replay are retried. A 524 means the origin stopped answering, not
 * that it stopped working — a timed-out booking POST may already have been applied, and retrying it
 * would double-book. Cal's own idempotency is not guaranteed across endpoints, so stay on reads.
 */
const REPLAYABLE_METHODS = ["GET", "HEAD", "OPTIONS"];

/** Request timeout; the one 4xx that means "try again" rather than "this request is wrong". */
const REQUEST_TIMEOUT_STATUS = 408;

interface CalTransientErrorInput {
  error: unknown;
  /** The request's HTTP method. `undefined` means fetch's default of GET. */
  method?: string;
}

/**
 * True when a Cal request failed for a reason that is likely to resolve on its own and the request
 * can be safely replayed. Rate limits are excluded: they are transient too, but they carry a much
 * longer budget and the caller classifies them separately.
 */
export function isRetryableCalTransientError({ error, method }: CalTransientErrorInput): boolean {
  if (CalApiRateLimitError.isInstance(error)) {
    return false;
  }

  if (!REPLAYABLE_METHODS.includes(method ?? "GET")) {
    return false;
  }

  if (error instanceof CalApiError) {
    return error.statusCode >= 500 || error.statusCode === REQUEST_TIMEOUT_STATUS;
  }

  // undici raises TypeError("fetch failed") for DNS/socket/TLS faults, before any response exists.
  // Anything else thrown here is one of our own precondition errors and would fail identically.
  return error instanceof TypeError;
}
